/**
 * i18next internationalisation configuration.
 *
 * Sets up five languages (nl, en, de, fr, es) with browser-based
 * language detection and localStorage caching. After initialisation,
 * any admin-created translation overrides stored in localStorage are
 * merged on top of the bundled locale files.
 *
 * @module i18n
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import nl from './locales/nl.json';
import en from './locales/en.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import es from './locales/es.json';

/** Bundled translation resources keyed by language code. */
const resources = {
  nl: { translation: nl },
  en: { translation: en },
  de: { translation: de },
  fr: { translation: fr },
  es: { translation: es },
};

/** Map TLD to default language: .nl → Dutch, .com → English. */
const hostnameDetector = {
  name: 'hostname',
  lookup(): string | undefined {
    const host = window.location.hostname;
    if (host.endsWith('.nl')) return 'nl';
    if (host.endsWith('.com')) return 'en';
    return undefined;
  },
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(hostnameDetector);

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'nl',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'hostname', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
  });

/*
 * Apply any saved translation overrides from the admin panel.
 * Overrides are stored as flat dot-separated keys per language
 * and are deep-merged into the existing resource bundles.
 */
try {
  const raw = localStorage.getItem('booge_it_translation_overrides');
  if (raw) {
    const overrides: Record<string, Record<string, string>> = JSON.parse(raw);
    for (const [lang, flatMap] of Object.entries(overrides)) {
      const nested: Record<string, unknown> = {};
      for (const [flatKey, val] of Object.entries(flatMap)) {
        const parts = flatKey.split('.');
        let cur: Record<string, unknown> = nested;
        for (let i = 0; i < parts.length - 1; i++) {
          if (!cur[parts[i]] || typeof cur[parts[i]] !== 'object') {
            cur[parts[i]] = {};
          }
          cur = cur[parts[i]] as Record<string, unknown>;
        }
        cur[parts[parts.length - 1]] = val;
      }
      i18n.addResourceBundle(lang, 'translation', nested, true, true);
    }
  }
} catch { /* ignore corrupt overrides */ }

export default i18n;
